import type { QuestionImage } from "../types"
import { imageUrl } from "./lib/api"

interface QuestionPictureProps {
	image: QuestionImage
	// "board" is the play surface's big centred picture; "inline" is the
	// thumbnail-sized one that sits next to a prompt in the round review.
	variant: "board" | "inline"
}

// A lone emoji is the whole picture and gets to be huge; a counting question
// shows up to nine of them and has to stay small enough to fit and be
// countable at a glance. dvh, not vh: on a phone browser vh measures the
// viewport with the chrome retracted, so a vh-sized glyph overflows the space
// the board actually has (text cannot shrink to fit the way the flag image
// does, so these numbers have to be right on their own).
//
// The line-height is the font's own (`normal`), not 1: a colour-emoji glyph's
// box is taller than its em square (about 1.3em on Apple Color Emoji, 1.2em on
// Noto), and a shorter line box does not hide that — the browser still counts
// the glyph box as overflow, and the prompt half above the options is a
// scroll container, so it grew a scrollbar a few pixels tall on every animal
// question. Clipping instead would cut the glyph on Android, where the ink
// really is taller than 1em. The dvh terms are sized for that taller box, so
// a lone emoji is about a quarter of the screen and a row of nine about a
// tenth — set the box, not the em square, when tuning these.
function boardEmojiStyle(emoji: string): string {
	const size =
		[...emoji].length === 1
			? "clamp(4rem, 20dvh, 11rem)"
			: "clamp(2rem, 8dvh, 4.5rem)"
	return `font-size: ${size}; line-height: normal`
}

// The single place that turns a question's image descriptor into pixels.
// Every form — asset-backed (flag/shape/animal) and inline emoji — must be
// handled here, because imageUrl() throws for anything with no URL;
// rendering an image anywhere else risks re-introducing that crash.
export function QuestionPicture({ image, variant }: QuestionPictureProps) {
	if (image.kind === "emoji") {
		const emoji = image.emoji ?? ""
		// aria-hidden for the same reason flags carry an empty alt: the
		// emoji's accessible name ("giraffe") is the answer.
		return (
			<span
				class={
					variant === "board" ? "flex wrap jc-center center gap-2" : "ml-2"
				}
				style={
					variant === "board" ? boardEmojiStyle(emoji) : "font-size: 1.5em"
				}
				aria-hidden="true"
			>
				{/* One span per emoji so a counting question wraps evenly instead
				    of breaking mid-run. */}
				{[...emoji].map((glyph, i) => (
					// Repeated glyphs, so position is the only identity available.
					<span key={i}>{glyph}</span>
				))}
			</span>
		)
	}

	if (image.kind === "shape") {
		// A silhouette is not a picture but a shape, so it is drawn as a CSS
		// mask over currentColor (see .shape in styles.css): the file only says
		// *where* to paint, the text colour says what with, and dark mode needs
		// no inverting. An <img> would show the file's own black fill.
		return (
			<span
				class={`shape ${variant === "board" ? "board-image" : "ml-2"}`}
				style={`--shape: url(${imageUrl(image)});${
					variant === "inline" ? " height: 1.5em;" : ""
				}`}
				aria-hidden="true"
			/>
		)
	}

	return (
		<img
			src={imageUrl(image)}
			// Flags contain white fields, so they need their own border to read
			// as a flag rather than a cropped stripe on a light page. The same
			// border/radius reads fine on an animal illustration's edge-to-edge
			// pastel background, so it is not worth a per-kind branch.
			class={`${variant === "board" ? "board-image" : "ml-2"} rounded border`}
			style={variant === "inline" ? "height: 1.5em;" : undefined}
			alt=""
		/>
	)
}
